import React from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle, Star } from "lucide-react";

interface LocationBennesSEOContentProps {
  showDepartmentLinks?: boolean;
}

const LocationBennesSEOContent: React.FC<LocationBennesSEOContentProps> = ({ 
  showDepartmentLinks = true 
}) => {
  const departments = [
    { code: "12", name: "Aveyron", cities: ["Rodez", "Millau", "Villefranche-de-Rouergue"] },
    { code: "15", name: "Cantal", cities: ["Aurillac", "Saint-Flour", "Mauriac"] },
    { code: "31", name: "Haute-Garonne", cities: ["Toulouse", "Muret", "Saint-Gaudens"] },
    { code: "46", name: "Lot", cities: ["Cahors", "Figeac", "Gourdon"] },
    { code: "48", name: "Lozère", cities: ["Mende", "Marvejols", "Florac"] },
    { code: "63", name: "Puy-de-Dôme", cities: ["Clermont-Ferrand", "Riom", "Thiers"] },
    { code: "81", name: "Tarn", cities: ["Albi", "Castres", "Mazamet"] },
    { code: "95", name: "Val-d'Oise", cities: ["Cergy", "Argenteuil", "Sarcelles"] }
  ];

  const benneTypes = [
    {
      name: "Benne gravats 8m³",
      description: "Idéale pour évacuer béton, briques, tuiles, carrelage",
      price: "À partir de 120€",
      keywords: ["gravats", "béton", "démolition"]
    },
    {
      name: "Benne tout-venant 15m³", 
      description: "Parfaite pour déchets ménagers, encombrants, DIB",
      price: "À partir de 180€",
      keywords: ["encombrants", "déménagement", "rénovation"]
    },
    {
      name: "Benne déchets verts 20m³",
      description: "Spécialisée pour branches, feuilles, tontes de gazon",
      price: "À partir de 160€",
      keywords: ["élagage", "jardinage", "paysagiste"]
    },
    {
      name: "Benne grande capacité 30m³",
      description: "Pour gros chantiers, entreprises BTP, collectivités",
      price: "À partir de 250€",
      keywords: ["chantier", "BTP", "professionnel"]
    }
  ];

  const scrollToRequestForm = () => {
    const formElement = document.getElementById('request-form');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="space-y-12">
      {/* Section principale SEO */}
      <section className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6">
          Location de bennes professionnelle - Service rapide et écologique
        </h2>
        <div className="prose max-w-none text-gray-700">
          <p className="text-lg mb-6 leading-relaxed">
            <strong>Groupe Fabre Environnement</strong> est votre partenaire de confiance pour la 
            <strong> location de bennes</strong> dans 8 départements français. Avec plus de 25 ans d'expérience 
            en gestion des déchets, nous proposons des <strong>bennes de qualité professionnelle</strong> 
            adaptées à tous vos projets : rénovation, construction, déménagement, jardinage.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold text-fabre-darkgreen mb-4 flex items-center">
                <CheckCircle className="h-6 w-6 text-fabre-green mr-2" />
                Pourquoi choisir nos bennes ?
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-fabre-green mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Livraison rapide</strong> : Service 24-48h dans nos zones d'intervention</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-fabre-green mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Tarifs transparents</strong> : Devis gratuit sans frais cachés</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-fabre-green mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Valorisation écologique</strong> : 85% de vos déchets recyclés</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-fabre-green mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Conseil expert</strong> : Aide au choix de la bonne taille</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-fabre-darkgreen mb-4">
                Types de projets
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-fabre-lightgreen p-3 rounded text-center">
                  <div className="font-medium text-fabre-darkgreen">Particuliers</div>
                  <div className="text-sm text-gray-600">Rénovation, jardinage</div>
                </div>
                <div className="bg-fabre-lightgreen p-3 rounded text-center">
                  <div className="font-medium text-fabre-darkgreen">Entreprises</div>
                  <div className="text-sm text-gray-600">Chantiers, bureaux</div>
                </div>
                <div className="bg-fabre-lightgreen p-3 rounded text-center">
                  <div className="font-medium text-fabre-darkgreen">Collectivités</div>
                  <div className="text-sm text-gray-600">Événements, nettoyage</div>
                </div>
                <div className="bg-fabre-lightgreen p-3 rounded text-center">
                  <div className="font-medium text-fabre-darkgreen">Syndics</div>
                  <div className="text-sm text-gray-600">Copropriétés, travaux</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section types de bennes avec prix */}
      <section className="bg-gradient-to-br from-fabre-lightgreen to-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
          Nos bennes disponibles - Tarifs 2024
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benneTypes.map((benne, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-semibold text-fabre-gray mb-3">{benne.name}</h3>
              <p className="text-gray-600 mb-4 text-sm">{benne.description}</p>
              <div className="text-xl font-bold text-fabre-green mb-3">{benne.price}</div>
              <div className="flex flex-wrap gap-1 mb-4">
                {benne.keywords.map((keyword, idx) => (
                  <span key={idx} className="text-xs bg-fabre-green text-white px-2 py-1 rounded">
                    {keyword}
                  </span>
                ))}
              </div>
              <button 
                onClick={scrollToRequestForm}
                className="w-full bg-fabre-green text-white py-2 rounded hover:bg-fabre-darkgreen transition-colors text-sm"
              >
                Réserver cette benne
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Section géolocalisée */}
      {showDepartmentLinks && (
        <section className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
            Location de bennes par département
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Découvrez nos services de location de bennes dans chaque département. 
            Nous adaptons nos solutions aux spécificités locales et aux besoins de chaque territoire.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept) => (
              <Link
                key={dept.code}
                to={`/nos-services/location-bennes/${dept.code}`}
                className="group bg-gradient-to-br from-fabre-lightgreen to-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-5 w-5 text-fabre-green" />
                  <h3 className="font-semibold text-fabre-gray">
                    {dept.name} ({dept.code})
                  </h3>
                </div>
                
                <div className="mb-3">
                  <div className="text-sm text-gray-600 mb-2">Principales villes :</div>
                  <div className="flex flex-wrap gap-1">
                    {dept.cities.slice(0, 2).map((city, index) => (
                      <span key={index} className="text-xs bg-fabre-green text-white px-2 py-1 rounded">
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="text-fabre-green group-hover:text-fabre-darkgreen transition-colors text-sm font-medium">
                  Voir nos services →
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Section FAQ SEO */}
      <section className="bg-gray-50 rounded-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6">
          Questions fréquentes - Location de bennes
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Quel est le prix d'une location de benne ?
              </h3>
              <p className="text-gray-700 text-sm">
                Nos tarifs démarrent à 120€ pour une benne 8m³. Le prix varie selon la taille, 
                la durée de location et le type de déchets. Demandez votre devis gratuit !
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Combien de temps puis-je garder la benne ?
              </h3>
              <p className="text-gray-700 text-sm">
                La durée standard est de 7 jours, extensible selon vos besoins. 
                Nous nous adaptons à votre planning de chantier.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Quels déchets puis-je mettre dans la benne ?
              </h3>
              <p className="text-gray-700 text-sm">
                Gravats, déchets verts, encombrants, DIB selon le type de benne. 
                Nous vous conseillons sur le tri pour optimiser la valorisation.
              </p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Dans quels départements intervenez-vous ?
              </h3>
              <p className="text-gray-700 text-sm">
                Nous couvrons 8 départements : Aveyron, Cantal, Haute-Garonne, Lot, 
                Lozère, Puy-de-Dôme, Tarn et Val-d'Oise avec livraison rapide.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Comment choisir la bonne taille de benne ?
              </h3>
              <p className="text-gray-700 text-sm">
                8m³ pour petits travaux, 15m³ pour rénovation moyenne, 30m³ pour gros chantiers. 
                Notre équipe vous aide à estimer le volume nécessaire.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-fabre-darkgreen mb-2">
                Que deviennent mes déchets après collecte ?
              </h3>
              <p className="text-gray-700 text-sm">
                85% de vos déchets sont valorisés dans nos centres de tri. 
                Nous privilégions le recyclage et la valorisation énergétique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section témoignages */}
      <section className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-6 text-center">
          Ils nous font confiance
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-fabre-lightgreen p-6 rounded-lg">
            <div className="flex items-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-gray-700 text-sm mb-4">
              "Service impeccable pour notre chantier à Rodez. Livraison ponctuelle, 
              tarif respecté et équipe professionnelle."
            </p>
            <div className="text-sm text-fabre-darkgreen font-medium">
              Jean M. - Entreprise BTP, Aveyron
            </div>
          </div>
          
          <div className="bg-fabre-lightgreen p-6 rounded-lg">
            <div className="flex items-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-gray-700 text-sm mb-4">
              "Parfait pour notre rénovation. Conseils avisés sur le choix de la benne 
              et collecte rapide."
            </p>
            <div className="text-sm text-fabre-darkgreen font-medium">
              Marie D. - Particulier, Toulouse
            </div>
          </div>
          
          <div className="bg-fabre-lightgreen p-6 rounded-lg">
            <div className="flex items-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-gray-700 text-sm mb-4">
              "Partenaire fiable pour nos chantiers municipaux. 
              Respect des délais et excellent rapport qualité-prix."
            </p>
            <div className="text-sm text-fabre-darkgreen font-medium">
              Commune de Castres, Tarn
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationBennesSEOContent;
